import { useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import {
  Aviso,
  Cabecalho,
  Campo,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import { IndicadoresDaCarteira } from "../componentes/console";
import { useTitulo } from "../nucleo/titulo";
import {
  ETAPAS,
  LIMITE_DA_CARTEIRA,
  rotuloDaVez,
  formatarCnpj,
  formatarData,
  useImplantacoes,
  type ItemDaListagem,
} from "../nucleo/implantacoes";
import { caminhoDaRevisao } from "../nucleo/kickoff";
import { caminhoDoCofre } from "../nucleo/cofre";
import { caminhoDaImplantacao } from "../nucleo/console";

/**
 * O console interno de implantações — a tabela da tela 10.
 *
 * A etapa de cada linha é derivada da linha do tempo no servidor, a mesma derivação que alimenta
 * o painel do cliente. É isso que impede o console de dizer "aguardando infra" enquanto o
 * cliente lê "concluído" na tela dele.
 *
 * O recorte padrão é a carteira de quem abriu: consultor e comercial veem as próprias, admin
 * interno e infraestrutura veem tudo. Carteira é recorte de trabalho, não fronteira de
 * segurança — a fronteira entre clientes é a política de linha do banco, e ela não depende
 * desta tela.
 *
 * **Ordenar pelo cabeçalho** (spec de 28/09, 6.6): data, consultor e etapa, clicando no título da
 * coluna — o primeiro clique ordena do menor para o maior, o segundo inverte. A ordem escolhida
 * vai na URL (`?ordem=…&sentido=…`), para que o link e a recarga a mantenham. Três decisões:
 *
 * · **"Data" é a entrega prevista**, que é a data que a tabela mostra e a que se cobra. A data de
 *   criação existe no dado, mas uma coluna ordenada por algo que ela não exibe pareceria
 *   embaralhada. Sem liberação do ambiente não há data: "a definir" vai para o fim nos dois
 *   sentidos, porque não é nem a mais cedo nem a mais tarde;
 * · **etapa segue a esteira** (contrato → infraestrutura → kickoff → execução), não o alfabeto;
 * · **consultor em branco ("a definir") também vai para o fim** nos dois sentidos.
 *
 * Sem ordem escolhida, vale a do servidor (as mais recentes primeiro). A ordenação é na tela: a
 * carteira chega inteira (`LIMITE_DA_CARTEIRA`), e a busca continua no servidor.
 */
type ColunaOrdenavel = "data" | "consultor" | "etapa";
type Sentido = "asc" | "desc";

const COLUNAS_ORDENAVEIS: readonly ColunaOrdenavel[] = ["data", "consultor", "etapa"];
const COMPARAR_TEXTO = new Intl.Collator("pt-BR", { sensitivity: "base" });

/** Compara dois valores que podem faltar: o que falta vai para o fim, qualquer que seja o sentido. */
function comAusenteNoFim<T>(
  a: T | null,
  b: T | null,
  sentido: Sentido,
  comparar: (x: T, y: T) => number,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const resultado = comparar(a, b);
  return sentido === "asc" ? resultado : -resultado;
}

function ordenarCarteira(
  itens: readonly ItemDaListagem[],
  ordem: ColunaOrdenavel,
  sentido: Sentido,
): ItemDaListagem[] {
  const porNome = (a: ItemDaListagem, b: ItemDaListagem) =>
    COMPARAR_TEXTO.compare(a.organizacao, b.organizacao);
  const criterio = (a: ItemDaListagem, b: ItemDaListagem): number => {
    if (ordem === "data") {
      // ISO `aaaa-mm-dd` ordena como texto.
      return comAusenteNoFim(a.entrega_prevista, b.entrega_prevista, sentido, (x, y) =>
        x.localeCompare(y),
      );
    }
    if (ordem === "consultor") {
      return comAusenteNoFim(a.consultor, b.consultor, sentido, (x, y) =>
        COMPARAR_TEXTO.compare(x, y),
      );
    }
    const posicao = (item: ItemDaListagem) => ETAPAS.indexOf(item.etapa_atual);
    const resultado = posicao(a) - posicao(b);
    return sentido === "asc" ? resultado : -resultado;
  };
  // Empate desempata pelo nome do cliente, sempre em ordem alfabética: a mesma lista, a mesma ordem.
  return [...itens].sort((a, b) => criterio(a, b) || porNome(a, b));
}

export function ListaDeImplantacoes() {
  useTitulo("Implantações");
  const [parametros, setParametros] = useSearchParams();
  const [busca, setBusca] = useState("");
  const implantacoes = useImplantacoes({ busca });
  const recemCriada = parametros.get("nova");

  const pedida = parametros.get("ordem");
  const ordem = COLUNAS_ORDENAVEIS.find((coluna) => coluna === pedida) ?? null;
  const sentido: Sentido = parametros.get("sentido") === "desc" ? "desc" : "asc";

  function ordenarPor(coluna: ColunaOrdenavel) {
    const proximos = new URLSearchParams(parametros);
    const novoSentido: Sentido = ordem === coluna && sentido === "asc" ? "desc" : "asc";
    proximos.set("ordem", coluna);
    proximos.set("sentido", novoSentido);
    setParametros(proximos, { replace: true });
  }

  const linhas =
    implantacoes.data && ordem ? ordenarCarteira(implantacoes.data, ordem, sentido) : implantacoes.data;

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="titulo-pagina">Implantações</h1>
          <p className="lead">
            A carteira, com a etapa atual de cada cliente e o prazo.
          </p>
        </div>
        <Link to="/admin/implantacoes/nova" className="botao">
          Nova implantação
        </Link>
      </div>

      {recemCriada ? (
        <Aviso tom="sucesso" className="mb-5" data-testid="implantacao-criada">
          Implantação criada e convite de acesso enviado ao contato. Ela já aparece na lista
          abaixo, na etapa de infraestrutura.
        </Aviso>
      ) : null}

      <IndicadoresDaCarteira />

      <div className="max-w-[420px] mb-4">
        <Campo
          rotulo="Buscar"
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          placeholder="Razão social, nome fantasia ou CNPJ"
        />
      </div>

      {implantacoes.isPending ? (
        <p className="lead" role="status">
          Carregando a carteira…
        </p>
      ) : implantacoes.isError ? (
        <Aviso tom="erro" data-testid="falha-da-listagem">
          Não foi possível carregar as implantações agora. Atualize a página em alguns instantes.
        </Aviso>
      ) : implantacoes.data.length === 0 ? (
        <Aviso tom="marca" data-testid="carteira-vazia">
          {busca.trim()
            ? "Nenhuma implantação encontrada para esta busca."
            : "Nenhuma implantação na sua carteira ainda. Comece cadastrando um cliente."}
        </Aviso>
      ) : (
        <Tabela legenda="Implantações da carteira, com etapa atual, prazo e responsável.">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Cliente</CelulaCabecalho>
              <CelulaCabecalho>Módulos</CelulaCabecalho>
              <Ordenavel coluna="etapa" ordem={ordem} sentido={sentido} aoOrdenar={ordenarPor}>
                Etapa atual
              </Ordenavel>
              <CelulaCabecalho>De quem é a vez</CelulaCabecalho>
              <Ordenavel coluna="data" ordem={ordem} sentido={sentido} aoOrdenar={ordenarPor}>
                Entrega prevista
              </Ordenavel>
              <Ordenavel coluna="consultor" ordem={ordem} sentido={sentido} aoOrdenar={ordenarPor}>
                Consultor
              </Ordenavel>
              <CelulaCabecalho>Kickoff</CelulaCabecalho>
              <CelulaCabecalho>Credenciais</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {(linhas ?? []).map((item) => (
              <LinhaDaImplantacao key={item.id} item={item} />
            ))}
          </Corpo>
        </Tabela>
      )}
      {implantacoes.data && implantacoes.data.length >= LIMITE_DA_CARTEIRA ? (
        <p className="nota-rodape mt-3" data-testid="carteira-no-limite">
          Mostrando as {LIMITE_DA_CARTEIRA} implantações mais recentes. Use a busca para encontrar
          as demais.
        </p>
      ) : null}
    </>
  );
}

function Ordenavel({
  coluna,
  ordem,
  sentido,
  aoOrdenar,
  children,
}: {
  coluna: ColunaOrdenavel;
  ordem: ColunaOrdenavel | null;
  sentido: Sentido;
  aoOrdenar: (coluna: ColunaOrdenavel) => void;
  children: ReactNode;
}) {
  const ativa = ordem === coluna;
  return (
    <CelulaCabecalho
      aria-sort={ativa ? (sentido === "asc" ? "ascending" : "descending") : "none"}
      data-testid={`ordenar-${coluna}`}
    >
      <button type="button" className="cabecalho-ordenavel" onClick={() => aoOrdenar(coluna)}>
        {children}
        {/* A seta é para quem vê; quem usa leitor de tela ouve o `aria-sort` do cabeçalho. */}
        <span aria-hidden="true" data-testid="seta-da-ordem">
          {ativa ? (sentido === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </CelulaCabecalho>
  );
}

function LinhaDaImplantacao({ item }: { item: ItemDaListagem }) {
  const entrega = formatarData(item.entrega_prevista);

  return (
    <Linha>
      <Celula className="celula-nome">
        {/* O nome é o caminho para a tela da implantação. Antes da F7 a carteira só levava à
            revisão e ao cofre: a esteira de um cliente, a linha do tempo e a liberação do
            ambiente não tinham por onde ser abertas, e quem clicava no nome não ia a lugar
            nenhum — o mesmo beco que a Amanda encontrou nos cards da esteira. */}
        <Link to={caminhoDaImplantacao(item.id)} data-testid="ver-implantacao">
          <span data-testid="nome-do-cliente">{item.organizacao}</span>
        </Link>
        <div className="celula-secundaria numero-tabular">{formatarCnpj(item.cnpj)}</div>
      </Celula>
      <Celula className="celula-secundaria">{item.modulos.join(", ") || "—"}</Celula>
      <Celula>{item.etapa_nome}</Celula>
      <Celula>
        <Chip estado={item.vez === "cliente" ? "aguardando" : "neutro"} data-testid="vez">
          {rotuloDaVez(item.vez, "interna")}
        </Chip>
      </Celula>
      <Celula className="numero-tabular" data-testid="entrega-da-linha">
        {/* Sem liberação do ambiente não existe data. "a definir" é a informação certa; um
            traço ou uma estimativa contariam outra coisa. */}
        {entrega ? (
          <>
            {entrega}
            {item.dias_restantes !== null ? (
              <div className="celula-secundaria">
                {item.dias_restantes >= 0
                  ? `${item.dias_restantes} dias restantes`
                  : `${Math.abs(item.dias_restantes)} dias em atraso`}
              </div>
            ) : null}
          </>
        ) : (
          <>
            a definir
            <div className="celula-secundaria">aguarda liberação e geração da tarefa de kickoff</div>
          </>
        )}
      </Celula>
      <Celula className="celula-secundaria">{item.consultor ?? "—"}</Celula>
      <Celula>
        {/* O caminho para as respostas e para as divergências entre trilhas. Sem ele, a
            contradição que o motor detecta desde a F5 só era alcançável por `curl`. */}
        <Link to={caminhoDaRevisao(item.id)} data-testid="ver-kickoff">
          Ver respostas
        </Link>
      </Celula>
      <Celula>
        {/* O cofre da tela 8. Sem esta coluna ele só era alcançável digitando a URL — e uma
            credencial que ninguém encontra continua sendo pedida por WhatsApp, que é o hábito
            que o cofre existe para substituir. */}
        <Link to={caminhoDoCofre(item.id)} data-testid="ver-cofre">
          Ver cofre
        </Link>
      </Celula>
    </Linha>
  );
}
